import * as THREE from 'three'
import {useFrame, useThree} from '@react-three/fiber'
import {Billboard, Plane} from '@react-three/drei'
import {useEffect, useMemo, useRef, useState} from 'react'
import {useSelectionTextures} from '../hooks/useSelectionTextures.jsx'
import {cornerRotations} from '../utils/constants.jsx'

export function SelectionHover({
                                   objectRef,
                                   hovered,
                                   cornerSize = 0.5,
                                   opacity = 1,
                                   padding = 0.02,
                               }) {
    const cornerRefs = useRef([])
    const textureSwitchTimeout = useRef(null) // Using timeout
    const isHoveringRef = useRef(false)

    const {selectionTexture1, selectionTexture2} = useSelectionTextures()
    const [activeTextureSet, setActiveTextureSet] = useState(0) // 0 for set1, 1 for set2
    const [paddingSign, setPaddingSign] = useState(-1)
    const {camera} = useThree()

    // --- Create Rotated Texture Clones ONCE ---
    const rotatedTextures = useMemo(() => {
        const createRotatedSet = (baseTexture) => {
            if (!baseTexture) return null
            return cornerRotations.map(rotation => {
                const clone = baseTexture.clone()
                clone.center.set(0.5, 0.5)
                clone.rotation = rotation
                clone.needsUpdate = true
                return clone
            })
        }

        return {
            set1: createRotatedSet(selectionTexture1),
            set2: createRotatedSet(selectionTexture2),
        }
    }, [selectionTexture1, selectionTexture2])

    // --- Dispose Cloned Textures on Unmount ---
    useEffect(() => {
        // Return cleanup function
        return () => {
            console.log('Selection Textures: Disposing cloned textures')
            rotatedTextures.set1?.forEach(texture => texture?.dispose())
            rotatedTextures.set2?.forEach(texture => texture?.dispose())
        }
    }, [rotatedTextures]) // Depend on the memoized object

    // --- Handle Texture Switching with Timeout ---
    useEffect(() => {
        isHoveringRef.current = hovered

        if (hovered) {
            const switchTexture = () => {
                if (!isHoveringRef.current) return // Stop if no longer hovered
                setActiveTextureSet(prev => (prev + 1) % 2) // Cycle 0, 1
                setPaddingSign(value => -1 * value)
                // Schedule next switch
                textureSwitchTimeout.current = setTimeout(switchTexture, 625)
            }
            // Start the first timeout (or restart if re-hovered)
            textureSwitchTimeout.current = setTimeout(switchTexture, 625)
        } else {
            // Clear timeout if un-hovered
            if (textureSwitchTimeout.current) {
                clearTimeout(textureSwitchTimeout.current)
            }
            // Optional: Reset to default state on un-hover
            setActiveTextureSet(1)
            setPaddingSign(1)
        }

        // Cleanup on unmount or hover change
        return () => {
            if (textureSwitchTimeout.current) {
                clearTimeout(textureSwitchTimeout.current)
            }
        }
    }, [hovered]) // Run effect when hover state changes

    // --- Reusable THREE objects ---
    const box = useMemo(() => new THREE.Box3(), [])
    const objectCenter = useMemo(() => new THREE.Vector3(), [])
    const vertices = useMemo(() => Array.from({length: 8}, () => new THREE.Vector3()), [])
    const screenCorners = useMemo(() => Array.from({length: 8}, () => new THREE.Vector3()), [])
    const worldCorners = useMemo(() => Array.from({length: 4}, () => new THREE.Vector3()), [])
    // const cameraForward = useMemo(() => new THREE.Vector3(), []) // Only needed for offset

    useEffect(() => {
        // Initial box calculation (optional refinement)
        if (hovered && objectRef.current) {
            try {
                box.setFromObject(objectRef.current, true)
            } catch (e) { /* handle error */
            }
        }
    }, [hovered, objectRef, box]) // Re-calc if hover starts

    // --- useFrame Logic ---
    useFrame(() => {
        // Visibility check first
        const billboardsVisible = hovered && cornerRefs.current?.length === 4 && objectRef.current
        cornerRefs.current.forEach(bb => bb && (bb.visible = billboardsVisible))

        if (!billboardsVisible) {
            return
        }

        // Bounds calculation logic (keep your existing logic here)
        objectRef.current.updateWorldMatrix(true, false)
        camera.updateMatrixWorld()

        try {
            box.setFromObject(objectRef.current, true)
            if (box.isEmpty()) throw new Error('Box empty')
            box.getCenter(objectCenter)
        } catch (e) {
            console.error('Error calculating box in frame:', e)
            cornerRefs.current.forEach(bb => bb && (bb.visible = false))
            return
        }

        const min = box.min
        const max = box.max
        // ... (populate vertices array as before) ...
        vertices[0].set(max.x, max.y, max.z)
        vertices[1].set(max.x, max.y, min.z)
        vertices[2].set(max.x, min.y, max.z)
        vertices[3].set(max.x, min.y, min.z)
        vertices[4].set(min.x, max.y, max.z)
        vertices[5].set(min.x, max.y, min.z)
        vertices[6].set(min.x, min.y, max.z)
        vertices[7].set(min.x, min.y, min.z)


        let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity
        let minZ_NDC = Infinity
        let pointInFront = false

        // ... (Project vertices, find min/max NDC coords as before) ...
        vertices.forEach((vertex, i) => {
            screenCorners[i].copy(vertex).project(camera)
            // Add robust check for visibility
            const z = screenCorners[i].z
            // const w = screenCorners[i].w // Check perspective divide result

            // Basic check: is it roughly within NDC cube? More robust checks might involve clipping.
            if (z > -1 && z < 1) {
                pointInFront = true
                minX = Math.min(minX, screenCorners[i].x)
                maxX = Math.max(maxX, screenCorners[i].x)
                minY = Math.min(minY, screenCorners[i].y)
                maxY = Math.max(maxY, screenCorners[i].y)
                minZ_NDC = Math.min(minZ_NDC, z)
            }
            // Handle points potentially behind camera but clipping edges are visible
            // (Your existing clamping logic might cover this, review if needed)
        })


        if (!pointInFront || minX === Infinity) { // Simplified check
            cornerRefs.current.forEach(bb => bb && (bb.visible = false))
            return
        }

        // ... (Apply padding as before) ...
        if (padding > 0) {
            // const aspect = state.viewport.width / state.viewport.height
            const paddingX = padding * 2 * paddingSign * (maxX - minX) // Scale padding relative to size
            const paddingY = padding * 2 * paddingSign * (maxY - minY)

            minX -= paddingX
            maxX += paddingX
            minY -= paddingY
            maxY += paddingY

            minX = Math.max(-1, minX) /* clamp */
            maxX = Math.min(1, maxX)
            minY = Math.max(-1, minY)
            maxY = Math.min(1, maxY)
        }


        const targetZ_NDC = Math.max(-1 + 1e-6, Math.min(1 - 1e-6, minZ_NDC - 1e-5))

        // ... (Unproject NDC corners to world space as before) ...
        worldCorners[0].set(minX, maxY, targetZ_NDC).unproject(camera) // Top-Left
        worldCorners[1].set(maxX, maxY, targetZ_NDC).unproject(camera) // Top-Right
        worldCorners[2].set(minX, minY, targetZ_NDC).unproject(camera) // Bottom-Left
        worldCorners[3].set(maxX, minY, targetZ_NDC).unproject(camera) // Bottom-Right


        // Position and scale billboards
        cornerRefs.current.forEach((billboard, index) => {
            if (!billboard) return
            // billboard.visible = true // Already set at the start of frame logic
            billboard.position.copy(worldCorners[index])
            billboard.scale.setScalar(cornerSize)
            billboard.updateMatrixWorld() // Needed if parent moves or for raycasting on billboard itself
        })
    })

    // --- Select the Correct Pre-Rotated Texture ---
    const currentTextureSet = activeTextureSet === 0 ? rotatedTextures.set2 : rotatedTextures.set1

    return (
        <>
            {/* Render Billboards */}
            {[...Array(4)].map((_, index) => {
                // Get the pre-rotated texture for this corner from the active set
                const cornerTexture = currentTextureSet ? currentTextureSet[index] : null

                return (
                    <Billboard
                        key={index}
                        ref={el => cornerRefs.current[index] = el}
                        visible={false} // Initial state, controlled by useFrame
                        name={`selection_corner#${index}`}
                    >
                        <Plane args={[1, 1]}>
                            <meshBasicMaterial
                                side={THREE.DoubleSide}
                                opacity={opacity}
                                transparent={true}
                                depthTest={false}
                                depthWrite={false}
                                map={cornerTexture} // Use the pre-rotated texture
                                toneMapped={false}
                            />
                        </Plane>
                    </Billboard>
                )
            })}
        </>
    )
}